import {
  userService,
  setAppliedStateVersion,
  reportRestrictionRefreshFailed,
} from '@cobu/api-client';
import { store } from '../../store';
import { setRestriction } from '../../store/settingsSlice';
import { logger } from '../../utils/logger';

/**
 * Single-flight refresh of the current user's restriction state. Module scope so
 * the api-client interceptor can trigger it without React context.
 *
 * This endpoint's own response carries `X-Session-State-Version` — adopting that
 * version below is what stops the interceptor re-triggering us in a loop.
 */

let isRefreshing = false;
let refreshRequestedAgain = false;

const run = async (): Promise<void> => {
  if (isRefreshing) {
    // Something changed mid-fetch — go again once this one lands.
    refreshRequestedAgain = true;
    return;
  }

  isRefreshing = true;
  refreshRequestedAgain = false;

  try {
    if (!store.getState().auth.token) {
      logger.warn('[Restriction] Refresh skipped — no session token');
      return;
    }

    // `blocked` is absent when unrestricted, which `isAllowed` in
    // useResidentRestrictions reads as "permitted" — so dispatch as-is.
    const restriction = await userService.fetchMyRestriction();

    store.dispatch(setRestriction(restriction));
    setAppliedStateVersion(restriction.stateVersion);
  } catch (err) {
    // Leave prior state intact — a failed refresh must not grant or revoke —
    // but reopen the version gate so the next response retries.
    reportRestrictionRefreshFailed();
    logger.error('[Restriction] Failed to refresh restriction state', err);
  } finally {
    isRefreshing = false;
    const again = refreshRequestedAgain;
    refreshRequestedAgain = false;
    if (again) void run();
  }
};

/** Request a refresh. Overlapping calls collapse via the single-flight flags. */
export const requestRestrictionRefresh = (): void => {
  void run();
};

/** Drop in-flight bookkeeping — call on teardown or session reset. */
export const resetRestrictionRefresh = (): void => {
  isRefreshing = false;
  refreshRequestedAgain = false;
};
